// Rules for including `patients_with_ehrid` rows in a /search run.
//
// /search has always queried `notes-c`, which holds VISITS — so a patient who
// exists only on the roster (a portal signup, a CSV import, anyone never
// scheduled) could not be found there at all. Searching both tables fixes that,
// but the two do not answer the same questions, and the gap has to be handled
// explicitly rather than by returning rows that quietly ignore a filter.
//
// Verified against the live schema: patients_with_ehrid has patient_name,
// email, ehr_id, vita_id and dob — and no `type`, `physician_name` or `tel_num`
// at all.

// Search-form column (a `prompts` key) → the roster column that answers it.
// Text columns are filtered with ilike; `dob` is a real `date` on both tables
// and goes through applyContainsFilters' date branch (eq / gte-lte range),
// never ilike — `operator does not exist: date ~~* unknown` was what used to
// make any DOB search read as "no such patient".
export const ROSTER_SEARCHABLE_COLUMNS = {
  patient_name: 'patient_name',
  email: 'email',
  EHR_ID: 'ehr_id',
  // Text on both tables (NOT NULL here, trigger-stamped on notes-c).
  vita_id: 'vita_id',
  dob: 'dob',
};

const isFilled = (v) => typeof v === 'string' && v.trim() !== '';

/**
 * Decide whether the roster can answer this search, and with which filters.
 *
 * All-or-nothing on purpose. If the search constrains anything the roster
 * cannot express, the roster contributes NOTHING rather than a set of rows that
 * satisfy some of what was typed — a phone-number search that quietly returned
 * patients whose phone was never checked would be worse than no roster results.
 *
 * @returns {{include: boolean, filters: Array<[string,string]>, unsupported: string[]}}
 *          `unsupported` names what forced the skip, so the UI can say why.
 */
export function planRosterSearch({ searchValues = {}, startDate = '', endDate = '' } = {}) {
  const active = Object.entries(searchValues || {}).filter(([, v]) => isFilled(v));
  // Unknown columns block rather than being dropped: if `prompts` grows a field
  // later, the roster stops answering instead of silently ignoring it.
  const unsupported = active
    .map(([col]) => col)
    .filter((col) => !ROSTER_SEARCHABLE_COLUMNS[col]);
  // A roster row is patient-level and has no appointment date to compare.
  if (startDate || endDate) unsupported.push('date range');

  if (unsupported.length > 0) return { include: false, filters: [], unsupported };
  return {
    include: true,
    filters: active.map(([col, v]) => [ROSTER_SEARCHABLE_COLUMNS[col], v.trim()]),
    unsupported: [],
  };
}

/**
 * Order the combined result set.
 *
 * Visits stay newest-first, as they have always been. Roster rows have no date
 * at all, so they cannot be interleaved meaningfully — they sort to the end,
 * alphabetically, as a "patient records" tail rather than salted through the
 * dated rows where a blank date column would look like missing data.
 *
 * Every matching row from both tables is kept. A patient who has visits AND a
 * record of their own appears as both: the rows are different things and open
 * different pages, and hiding the record made a patient who was plainly in
 * patients_with_ehrid look absent from a search that had just found them.
 */
export function sortSearchResults(rows = []) {
  return [...rows].sort((a, b) => {
    const aRoster = a?._source === 'roster';
    const bRoster = b?._source === 'roster';
    if (aRoster !== bRoster) return aRoster ? 1 : -1;
    if (aRoster) {
      return String(a?.patient_name || '').localeCompare(String(b?.patient_name || ''));
    }
    return String(b?.date_ || '').localeCompare(String(a?.date_ || ''));
  });
}
